import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type {
  ChecklistTemplateItem,
  DBChecklistTemplate,
  MeetingType,
} from "@/lib/db-types";

const MEETING_TYPES: readonly MeetingType[] = ["startup", "status", "debrief"];

export function useChecklistTemplates() {
  return useQuery({
    queryKey: ["checklist-templates"],
    queryFn: async (): Promise<DBChecklistTemplate[]> => {
      const { data, error } = await supabase
        .from("checklist_templates")
        .select("id, meeting_type, items, updated_at, updated_by")
        .order("meeting_type");
      if (error) throw new Error(error.message);
      return (data ?? []) as DBChecklistTemplate[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useChecklistTemplate(meetingType: MeetingType) {
  const { data, ...rest } = useChecklistTemplates();
  return {
    ...rest,
    data: data?.find((t) => t.meeting_type === meetingType) ?? null,
  };
}

export function useUpdateChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      meetingType,
      items,
      updatedBy,
    }: {
      meetingType: MeetingType;
      items: ChecklistTemplateItem[];
      updatedBy: string | null;
    }) => {
      if (!MEETING_TYPES.includes(meetingType)) {
        throw new Error(`Invalid meeting_type: ${meetingType}`);
      }
      const { error } = await supabase
        .from("checklist_templates")
        .update({ items, updated_by: updatedBy })
        .eq("meeting_type", meetingType);
      if (error) throw new Error(error.message);
    },
    onSettled: async () => {
      await invalidateFor(qc, "checklist_templates");
    },
  });
}
